import type React from "react"
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { RootState } from "../store";
import {
  addNewLandlordFarmMapping,
  fetchBillingEntityDetails,
  fetchCropYearDetails,
  fetchFarmDetails,
  fetchUnMappedFields,
  postNewBillingEntityDetails,
  postNewFarmDetails,
  postNewFarmNameDetails,
  renameBillingEntity,
  renameLandlord,
} from "../slices/addfarm-billingentity.slice";
import { mapDropdownOptions } from "../utils/mapDropDownOptions";

interface DropdownOptions {
  id: number;
  value: string;
  label: string;
}
const newFarmDetailsInfo = {
  farmName: "",
  billingEntity: "",
  cropYear: "",
};
const newBillingInfo = {
  billingEntityName: "",
};
const newFarmInfo = {
  farmName: "",
};

const defaultLandlordFieldMapping = {
  landlordId: null,
  fieldId: null,
  acre: null,
};

type LandlordFieldMappingInfo = {
  landlordId: number | null;
  fieldId: number | null;
  acre: number | null;
};

export const useAddFarm = () => {
  const [newFarmData, setNewFarmData] = useState(newFarmDetailsInfo);
  const [newLandlordFieldMapping, setNewLandlordFieldMapping] =
    useState<LandlordFieldMappingInfo>(defaultLandlordFieldMapping);
  const [fieldOptions, setFieldOptions] = useState<DropdownOptions[]>([]);
  const [newBillingData, setNewBillingData] = useState(newBillingInfo);
  const [newFarmName, setNewFarmName] = useState(newFarmInfo);
  const dispatch = useAppDispatch();
  const [billingNameError, setBillingNameError] = useState<string | null>(null);
  const [farmNameError, setFarmNameError] = useState<string | null>(null);
  const [farmDetailsError, setFarmDetailsError] = useState<string | null>(null);
  const [billingEntityError, setBillingEntityError] = useState<string | null>(
    null
  );
  const [cropYearError, setcropYearError] = useState<string | null>(null);
  const { billingEntities, farmNames, cropYear, fields } = useAppSelector(
    (state: RootState) => state.addfarm
  );
  const [landlordError, setLandlordError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [acresError, setAcresError] = useState<string | null>(null);

  useEffect(() => {
    if (billingEntities.length === 0) {
      dispatch(fetchBillingEntityDetails());
    }
    if (farmNames.length === 0) {
      dispatch(fetchFarmDetails());
    }
    if (cropYear.length === 0) {
      dispatch(fetchCropYearDetails());
    }
    if (fields.length === 0) {
      dispatch(fetchUnMappedFields());
    }
  }, [dispatch]);

  useEffect(() => {
    if (newFarmName.farmName) {
      setFarmNameError(null);
    }
  }, [newFarmName]);

  useEffect(() => {
    if (newBillingData.billingEntityName) {
      setBillingNameError(null);
    }
  }, [newBillingData]);

  useEffect(() => {
    if (newFarmData.billingEntity) {
      setBillingEntityError(null);
    }

    if (newFarmData.farmName) {
      setFarmDetailsError(null);
    }

    if (newFarmData.cropYear) {
      setcropYearError(null);
    }
  }, [newFarmData]);

  useEffect(() => {
    if (newLandlordFieldMapping.landlordId) {
      setLandlordError(null);
    }

    if (newLandlordFieldMapping.fieldId) {
      setFieldError(null);
    }

    if (newLandlordFieldMapping.acre) {
      setAcresError(null);
    }
  }, [newLandlordFieldMapping]);

  useEffect(() => {
    setFieldOptions(mapDropdownOptions(fields));
  }, [fields]);

  const farmNameOptions: DropdownOptions[] = mapDropdownOptions(farmNames);
  const billingEntityOptions: DropdownOptions[] =
    mapDropdownOptions(billingEntities);
  const cropYearOptions: DropdownOptions[] = mapDropdownOptions(cropYear);
  const handlebillingNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewBillingData((prevData) => ({
      ...prevData,
      billingEntityName: event.target.value,
    }));
  };

  const handlefarmNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFarmName((prevData) => ({
      ...prevData,
      farmName: event.target.value,
    }));
  };

  const handlefarmNameDropdownChange = (selectedOption: any) => {
    setNewFarmData((prevData) => ({
      ...prevData,
      farmName: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleBillingEntityChange = (selectedOption: any) => {
    setNewFarmData((prevData) => ({
      ...prevData,
      billingEntity: selectedOption ? selectedOption.value : "",
    }));
  };

  const handlecropYearChange = (selectedOption: any) => {
    setNewFarmData((prevData) => ({
      ...prevData,
      cropYear: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleFieldLandlordChange = (selectedOption: any) => {
    setNewLandlordFieldMapping((prevData) => ({
      ...prevData,
      landlordId: selectedOption ? selectedOption.id : null,
    }));
  };

  const handleFieldChange = (selectedOption: any) => {
    setNewLandlordFieldMapping((prevData) => ({
      ...prevData,
      fieldId: selectedOption ? selectedOption.id : null,
    }));
  };

  const handleAcreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const acre = e.target.value;
    setNewLandlordFieldMapping((prevData) => ({
      ...prevData,
      acre: acre ? Number(acre) : null,
    }));
  };

  const handleSaveBillingDetails = async () => {
    let hasError = false;

    if (!newBillingData.billingEntityName) {
      setBillingNameError("Billing Entity is required");
      hasError = true;
    }

    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }

    const response = await dispatch(
      postNewBillingEntityDetails({
        billingName: newBillingData.billingEntityName,
      })
    );

    if (response.meta.requestStatus === "fulfilled") {
      toast.success(response.payload);
      setNewBillingData(newBillingInfo);
      dispatch(fetchBillingEntityDetails());
    } else if (response.meta.requestStatus === "rejected") {
      toast.error("Billing Entity already exists!");
    }
  };

  const handleSaveFarmName = async () => {
    let hasError = false;

    if (!newFarmName.farmName) {
      setFarmNameError("Landlord Name is required");
      hasError = true;
    }

    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }

    const response = await dispatch(
      postNewFarmNameDetails({
        landlordName: newFarmName.farmName,
      })
    );

    if (response.meta.requestStatus === "fulfilled") {
      toast.success(response.payload);
      setNewFarmName(newFarmInfo);
      dispatch(fetchFarmDetails());
    } else if (response.meta.requestStatus === "rejected") {
      toast.error("Landlord Name already exists!");
    }
  };

  const handleSaveFarmDetails = async () => {
    let hasError = false;

    if (!newFarmData.farmName) {
      setFarmDetailsError("Landlord Name is required");
      hasError = true;
    }

    if (!newFarmData.billingEntity) {
      setBillingEntityError("Billing Entity is required");
      hasError = true;
    }

    if (!newFarmData.cropYear) {
      setcropYearError("Crop Year is required");
      hasError = true;
    }

    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }

    const response = await dispatch(
      postNewFarmDetails({
        landlordId: farmNameOptions
          .filter((option) => option.value === newFarmData.farmName)
          .map((option) => option.id)[0],
        calendarYear: cropYearOptions
          .filter((option) => option.value === newFarmData.cropYear)
          .map((option) => option.id)[0],
        partnerId: billingEntityOptions
          .filter((option) => option.value === newFarmData.billingEntity)
          .map((option) => option.id)[0],
      })
    );

    if (response.meta.requestStatus === "fulfilled") {
      toast.success(response.payload);
      setNewFarmData(newFarmDetailsInfo);
    } else if (response.meta.requestStatus === "rejected") {
      toast.error("Farm Details already exists!");
    }
  };

  const handleSaveLandlordFieldMapping = async () => {
    console.log(newLandlordFieldMapping);
    let hasError = false;
    if (!newLandlordFieldMapping.landlordId) {
      setLandlordError("Landlord is required");
      hasError = true;
    }

    if (!newLandlordFieldMapping.fieldId) {
      setFieldError("Field is required");
      hasError = true;
    }

    if (!newLandlordFieldMapping.acre) {
      setAcresError("Acres is required");
      hasError = true;
    }

    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }

    const response = await dispatch(
      addNewLandlordFarmMapping({ ...newLandlordFieldMapping })
    );

    if (response.meta.requestStatus === "fulfilled") {
      toast.success("Landlord mapped to Field successfully");
      setNewLandlordFieldMapping(defaultLandlordFieldMapping);
      dispatch(fetchUnMappedFields());
    } else if (response.meta.requestStatus === "rejected") {
      toast.error("Something went wrong. Please re-try");
    }
  };

  const handleCancel = () => {
    setNewFarmData(newFarmDetailsInfo);
    setBillingEntityError(null);
    setFarmDetailsError(null);
    setcropYearError(null);
  };

  // ---------- Rename state ----------
  const [selectedLandlordToRename, setSelectedLandlordToRename] = useState<DropdownOptions | null>(null);
  const [newLandlordName, setNewLandlordName] = useState("");
  const [renameLandlordError, setRenameLandlordError] = useState<string | null>(null);

  const [selectedBillingEntityToRename, setSelectedBillingEntityToRename] = useState<DropdownOptions | null>(null);
  const [newBillingEntityName, setNewBillingEntityName] = useState("");
  const [renameBillingEntityError, setRenameBillingEntityError] = useState<string | null>(null);

  const handleRenameLandlord = async () => {
    let hasError = false;
    if (!selectedLandlordToRename) {
      setRenameLandlordError("Please select a landlord");
      hasError = true;
    }
    if (!newLandlordName.trim()) {
      setRenameLandlordError("New name is required");
      hasError = true;
    }
    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }
    const response = await dispatch(
      renameLandlord({ id: selectedLandlordToRename!.id, landlordName: newLandlordName.trim() })
    );
    if (response.meta.requestStatus === "fulfilled") {
      toast.success("Landlord renamed successfully");
      setSelectedLandlordToRename(null);
      setNewLandlordName("");
      setRenameLandlordError(null);
      dispatch(fetchFarmDetails());
    } else {
      toast.error("Failed to rename landlord");
    }
  };

  const handleRenameBillingEntity = async () => {
    let hasError = false;
    if (!selectedBillingEntityToRename) {
      setRenameBillingEntityError("Please select a billing entity");
      hasError = true;
    }
    if (!newBillingEntityName.trim()) {
      setRenameBillingEntityError("New name is required");
      hasError = true;
    }
    if (hasError) {
      toast.error("Please fill in all required fields");
      return;
    }
    const response = await dispatch(
      renameBillingEntity({ id: selectedBillingEntityToRename!.id, billingName: newBillingEntityName.trim() })
    );
    if (response.meta.requestStatus === "fulfilled") {
      toast.success("Billing entity renamed successfully");
      setSelectedBillingEntityToRename(null);
      setNewBillingEntityName("");
      setRenameBillingEntityError(null);
      dispatch(fetchBillingEntityDetails());
    } else {
      toast.error("Failed to rename billing entity");
    }
  };

  return {
    newFarmData,
    setNewFarmData,
    newFarmName,
    setNewFarmName,
    newBillingData,
    setNewBillingData,
    billingNameError,
    setBillingNameError,
    farmNameError,
    setFarmNameError,
    farmDetailsError,
    setFarmDetailsError,
    billingEntityError,
    setBillingEntityError,
    cropYearError,
    setcropYearError,
    farmNameOptions,
    billingEntityOptions,
    cropYearOptions,
    fieldOptions,
    newLandlordFieldMapping,
    landlordError,
    fieldError,
    acresError,
    handlebillingNameChange,
    handlefarmNameChange,
    handlefarmNameDropdownChange,
    handleBillingEntityChange,
    handlecropYearChange,
    handleSaveFarmDetails,
    handleSaveBillingDetails,
    handleSaveFarmName,
    handleCancel,
    handleFieldLandlordChange,
    handleFieldChange,
    handleAcreChange,
    handleSaveLandlordFieldMapping,
    // Rename
    selectedLandlordToRename,
    setSelectedLandlordToRename,
    newLandlordName,
    setNewLandlordName,
    renameLandlordError,
    setRenameLandlordError,
    handleRenameLandlord,
    selectedBillingEntityToRename,
    setSelectedBillingEntityToRename,
    newBillingEntityName,
    setNewBillingEntityName,
    renameBillingEntityError,
    setRenameBillingEntityError,
    handleRenameBillingEntity,
  };
};

